import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import moment from 'moment';
import { ExamPaper } from './examPaperService';

// ---------------------------------------------------------------------------
// Branding constants
// ---------------------------------------------------------------------------
const BRAND = {
  name: 'The eAbhyas Academy',
  tagline: 'Empowering Schools · Building Future Achievers',
  address: 'www.eabhyasacademy.com',
  phone: '9030779982',
  email: 'eabhyasacademy@gmail.com',
  // Logo hosted on their website – loaded at runtime for PDF
  logoUrl: 'https://www.eabhyasacademy.com/images/logo.png',
  primaryColor: [30, 89, 163] as [number, number, number],   // brand blue
  accentColor: [255, 140, 0] as [number, number, number],    // brand orange
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function formatDate(iso: string) {
  return moment(iso).format('D MMM YYYY');
}

function fetchLogoAsBase64(): Promise<string | null> {
  return new Promise(resolve => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) { resolve(null); return; }
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      } catch { resolve(null); }
    };
    img.onerror = () => resolve(null);
    img.src = BRAND.logoUrl;
  });
}

// ---------------------------------------------------------------------------
// Excel export
// ---------------------------------------------------------------------------
export function exportExamPapersToExcel(papers: ExamPaper[], filtered: ExamPaper[]) {
  const wb = XLSX.utils.book_new();

  // ── Sheet 1: Summary ────────────────────────────────────────────────────
  const summaryRows: (string | number)[][] = [
    [BRAND.name],
    [BRAND.tagline],
    [`${BRAND.address}  |  ${BRAND.phone}  |  ${BRAND.email}`],
    [],
    ['Exam Papers Report'],
    [`Generated on: ${moment().format('D MMM YYYY, h:mm A')}`],
    [`Total papers: ${papers.length}   |   Showing: ${filtered.length}   |   Released: ${papers.filter(p => p.status === 1).length}   |   Draft: ${papers.filter(p => p.status === 0).length}`],
    [],
    // Header row
    ['#', 'Title', 'Exam Name', 'Subject', 'Duration (min)', 'Total Marks', 'Questions', 'Status', 'Created On', 'Description'],
  ];

  filtered.forEach((p, i) => {
    summaryRows.push([
      i + 1,
      p.title,
      p.examName,
      p.subject ?? '—',
      p.durationMins,
      p.totalMarks,
      p.questionCount,
      p.status === 1 ? 'Released' : 'Draft',
      formatDate(p.createdOn),
      p.description ?? '',
    ]);
  });

  const ws = XLSX.utils.aoa_to_sheet(summaryRows);

  // Column widths
  ws['!cols'] = [
    { wch: 5 }, { wch: 36 }, { wch: 20 }, { wch: 16 }, { wch: 14 },
    { wch: 12 }, { wch: 11 }, { wch: 11 }, { wch: 14 }, { wch: 40 },
  ];

  // Merge brand header cells A1:J1, A2:J2, A3:J3
  ws['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: 9 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: 9 } },
    { s: { r: 2, c: 0 }, e: { r: 2, c: 9 } },
    { s: { r: 4, c: 0 }, e: { r: 4, c: 9 } },
    { s: { r: 5, c: 0 }, e: { r: 5, c: 9 } },
    { s: { r: 6, c: 0 }, e: { r: 6, c: 9 } },
  ];

  XLSX.utils.book_append_sheet(wb, ws, 'Exam Papers');

  // ── Sheet 2: Stats ───────────────────────────────────────────────────────
  const statRows: (string | number)[][] = [
    [BRAND.name],
    ['Statistics Summary'],
    [],
    ['Metric', 'Value'],
    ['Total Papers', papers.length],
    ['Released', papers.filter(p => p.status === 1).length],
    ['Draft', papers.filter(p => p.status === 0).length],
    ['Total Questions', papers.reduce((s, p) => s + p.questionCount, 0)],
    ['Total Marks (all papers)', papers.reduce((s, p) => s + p.totalMarks, 0)],
    ['Avg Duration (min)', papers.length ? Math.round(papers.reduce((s, p) => s + p.durationMins, 0) / papers.length) : 0],
    ['Avg Questions/Paper', papers.length ? Math.round(papers.reduce((s, p) => s + p.questionCount, 0) / papers.length) : 0],
    [],
    ['Unique Exams', Array.from(new Set(papers.map(p => p.examName))).join(', ')],
    ['Unique Subjects', Array.from(new Set(papers.map(p => p.subject).filter(Boolean))).join(', ')],
  ];
  const ws2 = XLSX.utils.aoa_to_sheet(statRows);
  ws2['!cols'] = [{ wch: 28 }, { wch: 50 }];
  ws2['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: 1 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: 1 } },
  ];
  XLSX.utils.book_append_sheet(wb, ws2, 'Statistics');

  XLSX.writeFile(wb, `eAbhyas_ExamPapers_${moment().format('YYYYMMDD_HHmm')}.xlsx`);
}

// ---------------------------------------------------------------------------
// PDF export
// ---------------------------------------------------------------------------
export async function exportExamPapersToPdf(papers: ExamPaper[], filtered: ExamPaper[]) {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  const logoBase64 = await fetchLogoAsBase64();

  // ── Watermark helper ────────────────────────────────────────────────────
  function addWatermark() {
    doc.saveGraphicsState();
    // @ts-ignore – jsPDF internals
    doc.setGState(new doc.GState({ opacity: 0.06 }));
    doc.setTextColor(BRAND.primaryColor[0], BRAND.primaryColor[1], BRAND.primaryColor[2]);
    doc.setFontSize(52);
    doc.setFont('helvetica', 'bold');
    // Diagonal text centred on page
    doc.text('eAbhyas Academy', pageW / 2, pageH / 2, {
      align: 'center', angle: 35, baseline: 'middle',
    });
    doc.restoreGraphicsState();
    doc.setTextColor(0, 0, 0);
  }

  // ── Header on every page ─────────────────────────────────────────────────
  function addHeader(pageNumber: number, totalPages: number) {
    addWatermark();

    // Blue top bar
    doc.setFillColor(...BRAND.primaryColor);
    doc.rect(0, 0, pageW, 18, 'F');

    // Logo
    if (logoBase64) {
      try { doc.addImage(logoBase64, 'PNG', 4, 1.5, 28, 15); } catch { /* skip if load fails */ }
    }

    // Brand name
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.text(BRAND.name, logoBase64 ? 36 : 6, 8);

    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'normal');
    doc.text(`${BRAND.address}  ·  ${BRAND.phone}  ·  ${BRAND.email}`, logoBase64 ? 36 : 6, 13.5);

    // Page number right side
    doc.setFontSize(8);
    doc.text(`Page ${pageNumber} of ${totalPages}`, pageW - 6, 10, { align: 'right' });

    // Orange accent line
    doc.setFillColor(...BRAND.accentColor);
    doc.rect(0, 18, pageW, 1.2, 'F');

    doc.setTextColor(0, 0, 0);
  }

  // ── Cover / meta section ─────────────────────────────────────────────────
  // We first build the table to know total pages; jsPDF autotable handles that
  // We'll use a starting y after the header.

  const releasedCount = papers.filter(p => p.status === 1).length;
  const draftCount = papers.filter(p => p.status === 0).length;
  const totalQuestions = papers.reduce((s, p) => s + p.questionCount, 0);

  // Table body
  const body = filtered.map((p, i) => [
    String(i + 1),
    p.title,
    p.examName,
    p.subject ?? '—',
    `${p.durationMins} min`,
    String(p.totalMarks),
    String(p.questionCount),
    p.status === 1 ? 'Released' : 'Draft',
    formatDate(p.createdOn),
  ]);

  // Temp render to get page count estimate – we just let autoTable paginate
  let currentPage = 1;

  autoTable(doc, {
    startY: 30,
    head: [['#', 'Title', 'Exam Name', 'Subject', 'Duration', 'Marks', 'Questions', 'Status', 'Created']],
    body,
    theme: 'striped',
    headStyles: {
      fillColor: BRAND.primaryColor,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 8.5,
    },
    alternateRowStyles: { fillColor: [245, 248, 255] },
    bodyStyles: { fontSize: 8 },
    columnStyles: {
      0: { cellWidth: 8, halign: 'center' },
      1: { cellWidth: 52 },
      2: { cellWidth: 30 },
      3: { cellWidth: 24 },
      4: { cellWidth: 18, halign: 'center' },
      5: { cellWidth: 16, halign: 'center' },
      6: { cellWidth: 18, halign: 'center' },
      7: { cellWidth: 20, halign: 'center' },
      8: { cellWidth: 22, halign: 'center' },
    },
    // Colour Status column cells
    didParseCell(data) {
      if (data.column.index === 7 && data.section === 'body') {
        if (data.cell.raw === 'Released') {
          data.cell.styles.textColor = [27, 127, 55];
          data.cell.styles.fontStyle = 'bold';
        } else {
          data.cell.styles.textColor = [100, 100, 100];
        }
      }
    },
    didDrawPage(hookData) {
      const totalPages = (doc as any).internal.getNumberOfPages();
      addHeader(hookData.pageNumber, totalPages);

      // On first page only – report title & meta above the table
      if (hookData.pageNumber === 1) {
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(BRAND.primaryColor[0], BRAND.primaryColor[1], BRAND.primaryColor[2]);
        doc.text('Exam Papers Report', 10, 25);

        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(80, 80, 80);
        const meta = [
          `Generated: ${moment().format('D MMM YYYY, h:mm A')}`,
          `Total: ${papers.length} papers`,
          `Released: ${releasedCount}`,
          `Draft: ${draftCount}`,
          `Questions: ${totalQuestions}`,
          `Showing: ${filtered.length}`,
        ].join('    ');
        doc.text(meta, 10, 29.5);
      }
      currentPage = hookData.pageNumber;
    },
    margin: { top: 30, left: 10, right: 10, bottom: 14 },
  });

  // Fix headers on all pages now that we know total page count
  const totalPages = (doc as any).internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    addHeader(i, totalPages);
  }

  // ── Last page: Summary stats ──────────────────────────────────────────────
  doc.addPage();
  const sp = (doc as any).internal.getNumberOfPages();
  doc.setPage(sp);
  addHeader(sp, sp);

  let y = 30;
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(BRAND.primaryColor[0], BRAND.primaryColor[1], BRAND.primaryColor[2]);
  doc.text('Summary Statistics', 10, y);
  y += 8;

  const stats = [
    ['Total Papers', papers.length],
    ['Released', releasedCount],
    ['Draft', draftCount],
    ['Total Questions', totalQuestions],
    ['Avg Questions / Paper', papers.length ? Math.round(totalQuestions / papers.length) : 0],
    ['Total Marks (all)', papers.reduce((s, p) => s + p.totalMarks, 0)],
    ['Avg Duration (min)', papers.length ? Math.round(papers.reduce((s, p) => s + p.durationMins, 0) / papers.length) : 0],
  ];

  autoTable(doc, {
    startY: y,
    head: [['Metric', 'Value']],
    body: stats.map(r => [r[0], String(r[1])]),
    theme: 'grid',
    headStyles: { fillColor: BRAND.accentColor, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 9 },
    bodyStyles: { fontSize: 9 },
    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 70 }, 1: { cellWidth: 40 } },
    margin: { left: 10 },
  });

  // ── Footer on all pages ───────────────────────────────────────────────────
  const finalTotal = (doc as any).internal.getNumberOfPages();
  for (let i = 1; i <= finalTotal; i++) {
    doc.setPage(i);
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(120, 120, 120);
    doc.text(
      `© ${new Date().getFullYear()} ${BRAND.name}  ·  ${BRAND.tagline}  ·  ${BRAND.address}`,
      pageW / 2, pageH - 5, { align: 'center' }
    );
    doc.setDrawColor(200, 200, 200);
    doc.line(10, pageH - 8, pageW - 10, pageH - 8);
  }

  doc.save(`eAbhyas_ExamPapers_${moment().format('YYYYMMDD_HHmm')}.pdf`);
}

// ---------------------------------------------------------------------------
// Single exam paper export types (mirror DraftQuestion from builder)
// ---------------------------------------------------------------------------
export interface ExportOption {
  optionLabel: string;
  optionText: string;
  optionImage: string | null;
  isCorrect: boolean;
}
export interface ExportQuestion {
  questionNo: number;
  questionText: string;
  questionImage: string | null;
  marks: number;
  negativeMarks: number;
  questionType: 0 | 1 | 2;
  options: ExportOption[];
}

function qTypeName(t: 0 | 1 | 2) {
  return t === 0 ? 'MCQ' : t === 1 ? 'Multi-select' : 'True / False';
}

// ---------------------------------------------------------------------------
// Single paper – Excel
// ---------------------------------------------------------------------------
export function exportSingleExamPaperToExcel(paper: ExamPaper, questions: ExportQuestion[]) {
  const wb = XLSX.utils.book_new();
  const slug = paper.title.replace(/[^a-z0-9]/gi, '_').slice(0, 30);

  // ── Sheet 1: Paper Details ───────────────────────────────────────────────
  const infoRows: (string | number)[][] = [
    [BRAND.name],
    [BRAND.tagline],
    [`${BRAND.address}  |  ${BRAND.phone}  |  ${BRAND.email}`],
    [],
    [paper.title],
    ['Exam Name', paper.examName],
    ['Subject', paper.subject ?? '—'],
    ['Duration', `${paper.durationMins} minutes`],
    ['Total Marks', paper.totalMarks],
    ['Total Questions', questions.length],
    ['Status', paper.status === 1 ? 'Released' : 'Draft'],
    ['Created', formatDate(paper.createdOn)],
    ...(paper.description ? [['Description', paper.description]] : []),
    [],
    [`Generated on: ${moment().format('D MMM YYYY, h:mm A')}`],
  ];
  const wsInfo = XLSX.utils.aoa_to_sheet(infoRows);
  wsInfo['!cols'] = [{ wch: 18 }, { wch: 55 }];
  wsInfo['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: 4 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: 4 } },
    { s: { r: 2, c: 0 }, e: { r: 2, c: 4 } },
    { s: { r: 4, c: 0 }, e: { r: 4, c: 4 } },
  ];
  XLSX.utils.book_append_sheet(wb, wsInfo, 'Paper Details');

  // ── Sheet 2: Questions ───────────────────────────────────────────────────
  const qRows: (string | number)[][] = [
    [BRAND.name, '', '', '', '', '', '', ''],
    [paper.title, '', '', '', '', '', '', ''],
    [],
    ['Q#', 'Question Text', 'Type', 'Marks', 'Neg Marks', 'Option', 'Option Text', 'Correct?'],
  ];

  questions.forEach(q => {
    if (q.options.length === 0) {
      qRows.push([q.questionNo, q.questionText, qTypeName(q.questionType), q.marks, q.negativeMarks, '', '', '']);
    } else {
      q.options.forEach((opt, oi) => {
        qRows.push(
          oi === 0
            ? [q.questionNo, q.questionText, qTypeName(q.questionType), q.marks, q.negativeMarks, opt.optionLabel, opt.optionText, opt.isCorrect ? 'YES' : '']
            : ['', '', '', '', '', opt.optionLabel, opt.optionText, opt.isCorrect ? 'YES' : '']
        );
      });
    }
    // blank row between questions
    qRows.push([]);
  });

  const wsQ = XLSX.utils.aoa_to_sheet(qRows);
  wsQ['!cols'] = [
    { wch: 5 }, { wch: 60 }, { wch: 14 }, { wch: 8 }, { wch: 10 }, { wch: 8 }, { wch: 40 }, { wch: 9 },
  ];
  wsQ['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: 7 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: 7 } },
  ];
  XLSX.utils.book_append_sheet(wb, wsQ, 'Questions');

  // ── Sheet 3: Answer Key ──────────────────────────────────────────────────
  const akRows: (string | number)[][] = [
    [BRAND.name, '', '', ''],
    [`${paper.title} — Answer Key`, '', '', ''],
    [],
    ['Q#', 'Correct Answer(s)', 'Marks', 'Neg Marks'],
  ];
  questions.forEach(q => {
    const correct = q.options.filter(o => o.isCorrect).map(o => o.optionLabel).join(', ');
    akRows.push([q.questionNo, correct || '(none)', q.marks, q.negativeMarks]);
  });
  const wsAK = XLSX.utils.aoa_to_sheet(akRows);
  wsAK['!cols'] = [{ wch: 5 }, { wch: 28 }, { wch: 9 }, { wch: 10 }];
  wsAK['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: 3 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: 3 } },
  ];
  XLSX.utils.book_append_sheet(wb, wsAK, 'Answer Key');

  XLSX.writeFile(wb, `eAbhyas_${slug}_${moment().format('YYYYMMDD_HHmm')}.xlsx`);
}

// ---------------------------------------------------------------------------
// Single paper – PDF
// ---------------------------------------------------------------------------
export async function exportSingleExamPaperToPdf(paper: ExamPaper, questions: ExportQuestion[]) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const slug = paper.title.replace(/[^a-z0-9]/gi, '_').slice(0, 30);

  const logoBase64 = await fetchLogoAsBase64();

  function addWatermark() {
    doc.saveGraphicsState();
    // @ts-ignore
    doc.setGState(new doc.GState({ opacity: 0.06 }));
    doc.setTextColor(BRAND.primaryColor[0], BRAND.primaryColor[1], BRAND.primaryColor[2]);
    doc.setFontSize(44);
    doc.setFont('helvetica', 'bold');
    doc.text('eAbhyas Academy', pageW / 2, pageH / 2, { align: 'center', angle: 40, baseline: 'middle' });
    doc.restoreGraphicsState();
    doc.setTextColor(0, 0, 0);
  }

  function addHeader(pageNum: number, total: number) {
    addWatermark();
    doc.setFillColor(...BRAND.primaryColor);
    doc.rect(0, 0, pageW, 18, 'F');
    if (logoBase64) {
      try { doc.addImage(logoBase64, 'PNG', 3, 1.5, 24, 15); } catch { }
    }
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text(BRAND.name, logoBase64 ? 30 : 5, 8);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.text(`${BRAND.address}  ·  ${BRAND.phone}  ·  ${BRAND.email}`, logoBase64 ? 30 : 5, 13.5);
    doc.setFontSize(7.5);
    doc.text(`Page ${pageNum} of ${total}`, pageW - 5, 10, { align: 'right' });
    doc.setFillColor(...BRAND.accentColor);
    doc.rect(0, 18, pageW, 1, 'F');
    doc.setTextColor(0, 0, 0);
  }

  function addFooter() {
    const total = (doc as any).internal.getNumberOfPages();
    for (let i = 1; i <= total; i++) {
      doc.setPage(i);
      addHeader(i, total);
      doc.setFontSize(7);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(130, 130, 130);
      doc.text(
        `© ${new Date().getFullYear()} ${BRAND.name}  ·  ${BRAND.tagline}  ·  ${BRAND.address}`,
        pageW / 2, pageH - 5, { align: 'center' }
      );
      doc.setDrawColor(200, 200, 200);
      doc.line(10, pageH - 8, pageW - 10, pageH - 8);
      doc.setTextColor(0, 0, 0);
    }
  }

  // ── Page 1: Cover / meta ─────────────────────────────────────────────────
  addWatermark();
  addHeader(1, 1); // placeholder – fixed later

  let y = 26;

  // Paper title
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(BRAND.primaryColor[0], BRAND.primaryColor[1], BRAND.primaryColor[2]);
  doc.text(paper.title, 10, y);
  y += 7;

  // Orange underline
  doc.setFillColor(...BRAND.accentColor);
  doc.rect(10, y, pageW - 20, 0.8, 'F');
  y += 5;

  // Meta grid
  const metaItems = [
    ['Exam', paper.examName],
    ['Subject', paper.subject ?? '—'],
    ['Duration', `${paper.durationMins} minutes`],
    ['Total Marks', String(paper.totalMarks)],
    ['Questions', String(questions.length)],
    ['Status', paper.status === 1 ? 'Released' : 'Draft'],
    ['Created', formatDate(paper.createdOn)],
  ];
  const colW = (pageW - 20) / 2;
  doc.setFontSize(9);
  let metaY = y;
  metaItems.forEach((item, idx) => {
    const col = idx % 2;
    const row = Math.floor(idx / 2);
    const mx = 10 + col * colW;
    const my = metaY + row * 7;
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(60, 60, 60);
    doc.text(item[0] + ':', mx, my);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
    doc.text(item[1], mx + 26, my);
  });
  y = metaY + Math.ceil(metaItems.length / 2) * 7 + 4;

  if (paper.description) {
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(80, 80, 80);
    const descLines = doc.splitTextToSize(paper.description, pageW - 20);
    doc.text(descLines, 10, y);
    y += descLines.length * 5 + 3;
  }

  doc.setFillColor(...BRAND.primaryColor);
  doc.rect(10, y, pageW - 20, 0.5, 'F');
  y += 5;

  // ── Questions ─────────────────────────────────────────────────────────────
  const qBody: any[][] = [];

  questions.forEach(q => {
    const qLabel = `Q${q.questionNo}`;
    const typeInfo = `[${qTypeName(q.questionType)}  |  ${q.marks}M${q.negativeMarks > 0 ? `  -${q.negativeMarks}` : ''}]`;
    // Strip LaTeX markers for plain PDF (KaTeX not available)
    const qText = q.questionText.replace(/\$\$[\s\S]+?\$\$/g, '[formula]').replace(/\$[^$\n]+?\$/g, '[formula]');
    qBody.push([{ content: `${qLabel}  ${typeInfo}`, colSpan: 2, styles: { fontStyle: 'bold', fillColor: [235, 242, 255], textColor: BRAND.primaryColor as [number, number, number], fontSize: 8.5 } } as any]);
    qBody.push([{ content: qText, colSpan: 2, styles: { fontSize: 9, cellPadding: { left: 4, right: 4, top: 2, bottom: 2 } } } as any]);

    q.options.forEach(opt => {
      const optText = opt.optionText
        ? opt.optionText.replace(/\$\$[\s\S]+?\$\$/g, '[formula]').replace(/\$[^$\n]+?\$/g, '[formula]')
        : '(image only)';
      qBody.push([
        {
          content: opt.optionLabel,
          styles: {
            fontStyle: opt.isCorrect ? 'bold' : 'normal',
            fillColor: opt.isCorrect ? [232, 245, 233] : [255, 255, 255],
            textColor: opt.isCorrect ? [27, 127, 55] : [60, 60, 60],
            halign: 'center',
            cellWidth: 14,
          },
        },
        {
          content: optText + (opt.isCorrect ? '  ✓' : ''),
          styles: {
            fontStyle: opt.isCorrect ? 'bold' : 'normal',
            fillColor: opt.isCorrect ? [232, 245, 233] : [255, 255, 255],
            textColor: opt.isCorrect ? [27, 127, 55] : [40, 40, 40],
            fontSize: 8.5,
          },
        },
      ]);
    });

    // Spacer row
    qBody.push([{ content: '', colSpan: 2, styles: { cellPadding: 1, fillColor: [255, 255, 255] } } as any]);
  });

  autoTable(doc, {
    startY: y,
    body: qBody,
    theme: 'plain',
    columnStyles: { 0: { cellWidth: 14 }, 1: { cellWidth: pageW - 34 } },
    tableLineColor: [220, 220, 220],
    tableLineWidth: 0.2,
    didDrawPage(hookData) {
      const total = (doc as any).internal.getNumberOfPages();
      addHeader(hookData.pageNumber, total);
    },
    margin: { top: 22, left: 10, right: 10, bottom: 14 },
  });

  // ── Answer key appendix ───────────────────────────────────────────────────
  doc.addPage();
  const akPage = (doc as any).internal.getNumberOfPages();

  const akBody = questions.map(q => {
    const correct = q.options.filter(o => o.isCorrect).map(o => o.optionLabel).join(', ');
    return [String(q.questionNo), correct || '—', String(q.marks), String(q.negativeMarks)];
  });

  autoTable(doc, {
    startY: 26,
    head: [['Q#', 'Correct Answer(s)', 'Marks', '–ve Marks']],
    body: akBody,
    theme: 'striped',
    headStyles: { fillColor: BRAND.primaryColor, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 9 },
    alternateRowStyles: { fillColor: [245, 248, 255] },
    bodyStyles: { fontSize: 9 },
    columnStyles: {
      0: { cellWidth: 14, halign: 'center' },
      1: { cellWidth: 60 },
      2: { halign: 'center', cellWidth: 18 },
      3: { halign: 'center', cellWidth: 20 },
    },
    didDrawPage(hookData) {
      const total = (doc as any).internal.getNumberOfPages();
      addHeader(hookData.pageNumber, total);
      if (hookData.pageNumber === akPage) {
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(BRAND.primaryColor[0], BRAND.primaryColor[1], BRAND.primaryColor[2]);
        doc.text('Answer Key', 10, 24);
      }
    },
    margin: { top: 28, left: 10, right: 10, bottom: 14 },
  });

  addFooter();

  doc.save(`eAbhyas_${slug}_${moment().format('YYYYMMDD_HHmm')}.pdf`);
}
